export type StoreAccess = {
  accessMode: 'marketplace' | 'store';
  empresaid: string | null;
};

export type StoreEntry = StoreAccess & { lookup: string | null; slug: string | null; error: string };
export const STORE_ACCESS_KEY = 'storeAccess';
const LAST_STORE_ACCESS_KEY = 'lastStoreAccess';

export const normalizeEmpresaId = (value: unknown): string | null => {
  const raw = String(value || '').trim().replace(/^empresa#/, '');
  return /^[a-zA-Z0-9-]+$/.test(raw) ? `empresa#${raw}` : null;
};

const get = (storage: Storage, key: string) => {
  try { return storage.getItem(key); } catch { return null; }
};
const set = (storage: Storage, key: string, value: string | null) => {
  try { if (value === null) storage.removeItem(key); else storage.setItem(key, value); } catch { /* Storage indisponível: o contexto continua ativo. */ }
};
const parse = (raw: string | null): StoreAccess | null => {
  try {
    const value = JSON.parse(raw || 'null');
    if (!value || !['marketplace', 'store'].includes(value.accessMode)) return null;
    if (value.empresaid !== null && typeof value.empresaid !== 'string') return null;
    const empresaid = normalizeEmpresaId(value.empresaid);
    if ((value.empresaid !== null && !empresaid) || (value.accessMode === 'store' && !empresaid)) return null;
    return { accessMode: value.accessMode, empresaid };
  } catch { return null; }
};

export const readStoreAccess = (
  session: Storage = window.sessionStorage,
  legacy: Storage = window.localStorage
): StoreAccess => {
  const saved = parse(get(session, STORE_ACCESS_KEY)) || parse(get(legacy, LAST_STORE_ACCESS_KEY));
  if (saved) return saved;
  const empresaid = normalizeEmpresaId(get(legacy, 'empresaid'));
  const discovery = normalizeEmpresaId(get(legacy, 'discoveryStoreId'));
  return { accessMode: !empresaid || discovery === empresaid ? 'marketplace' : 'store', empresaid };
};

export const saveStoreAccess = (
  access: StoreAccess,
  session: Storage = window.sessionStorage,
  legacy: Storage = window.localStorage
) => {
  // Uma entrada direta só pode substituir a última seleção depois de identificar a loja.
  const valid = parse(JSON.stringify(access));
  if (!valid) return;
  const serialized = JSON.stringify(valid);
  set(session, STORE_ACCESS_KEY, serialized);
  set(legacy, LAST_STORE_ACCESS_KEY, serialized);
  // Compatibilidade com versões anteriores; a empresa ativa é lida da sessão da aba.
  set(legacy, 'empresaid', valid.empresaid);
  set(legacy, 'discoveryStoreId', null);
};

export const getActiveStoreId = () => readStoreAccess().empresaid;

export const resolveStoreEntry = (
  location: Pick<Location, 'hostname' | 'pathname' | 'search'> = window.location,
  session: Storage = window.sessionStorage,
  legacy: Storage = window.localStorage
): StoreEntry => {
  const subdomain = location.hostname.toLowerCase().split('.')[0];
  const ignored = ['localhost', 'xeguei', 'www', 'cliente', 'empresa', '127', '192', '[::1]'];
  const slug = subdomain && !ignored.includes(subdomain) && !/^\d+$/.test(subdomain) ? subdomain : null;
  if (slug) return { accessMode: 'store', empresaid: null, lookup: slug, slug, error: '' };

  const params = new URLSearchParams(location.search);
  const explicitPath = location.pathname.match(/^\/empresaid=(.*)$/i);
  const pathId = explicitPath ? explicitPath[1].replace(/\/$/, '')
    : location.pathname.match(/^\/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i)?.[1];
  if (params.has('empresaid') || pathId !== undefined) {
    let raw = params.has('empresaid') ? params.get('empresaid') : pathId;
    if (!params.has('empresaid')) {
      try { raw = decodeURIComponent(raw || ''); } catch { raw = null; }
    }
    const empresaid = normalizeEmpresaId(raw);
    return { accessMode: 'store', empresaid, lookup: empresaid, slug: null,
      error: empresaid ? '' : 'O link desta loja é inválido.' };
  }
  const saved = readStoreAccess(session, legacy);
  return { ...saved, lookup: saved.empresaid, slug: null,
    error: saved.accessMode === 'store' && !saved.empresaid ? 'Não foi possível identificar esta loja.' : '' };
};

export const getGuestStoreDestination = (entry: StoreEntry, pathname: string) => {
  if (entry.accessMode === 'marketplace') return entry.empresaid || pathname === '/pedido' ? '/pedido' : '/primeiroacesso';
  if (entry.slug || !entry.empresaid) return '/pedido';
  return `/pedido?empresaid=${encodeURIComponent(entry.empresaid.replace(/^empresa#/, ''))}`;
};

export const clearStoreOrderTerms = (storage: Storage = window.localStorage) => {
  ['taxaEntrega', 'tempoEntrega', 'tipoAgendamento', 'dataAgendamento', 'horaAgendamento']
    .forEach(key => set(storage, key, null));
};
